import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, useApiMutation, useApiQuery } from "../../api";
import type { Timesheet } from "../../api";
import { Button, Card, ConfirmDialog, Skeleton, Stat, useToast } from "../../components";
import { AsyncBoundary, PageHeader, ROUTES } from "../../shell";
import { Legende } from "./FeuilleFiche";
import { FeuilleStatut } from "./FeuilleStatut";
import { GrilleMois } from "./GrilleMois";
import { messageFeuille } from "./messages";
import { aujourdhuiLocal, depuisEntries, formatJours, libelleMois, parseMois, totalDemiJournees } from "./mois";
import { useAutoSave } from "./useAutoSave";
import styles from "./Feuilles.module.css";

/** La feuille d'un mois, remplie par son consultant. */
export function ConsultantFeuillePage() {
  const { sheetId = "" } = useParams();
  const chargement = useApiQuery((o) => api.getTimesheet(sheetId, o), [sheetId]);
  const [feuille, setFeuille] = useState<Timesheet | null>(null);

  useEffect(() => {
    setFeuille(chargement.data);
  }, [chargement.data]);

  return (
    <div className={styles.page}>
      <Link to={ROUTES.consultant} className={styles.filAriane}>
        ← Mes feuilles
      </Link>
      <AsyncBoundary
        state={chargement}
        errorTitle="Feuille illisible"
        skeleton={
          <div aria-busy="true">
            <Skeleton variant="title" width="20rem" />
            <Skeleton variant="block" height="18rem" />
          </div>
        }
      >
        {(chargee) => {
          const courante = feuille ?? chargee;
          const mois = parseMois(courante.period_month);
          return (
            <>
              <PageHeader
                eyebrow={courante.mission_label}
                title={mois === null ? courante.period_month : libelleMois(mois)}
                description={`Pour ${courante.company_name}. Un clic : demi-journée ; deux : journée ; trois : rien.`}
              />
              {courante.status === "draft" || courante.status === "rejected" ? (
                <Saisie key={courante.id + courante.status} feuille={courante} onChange={setFeuille} />
              ) : (
                <Lecture feuille={courante} />
              )}
            </>
          );
        }}
      </AsyncBoundary>
    </div>
  );
}

function Lecture({ feuille }: { feuille: Timesheet }) {
  const mois = parseMois(feuille.period_month);
  return (
    <div className={styles.fiche}>
      <FeuilleStatut feuille={feuille} />
      <Stat label="Jours déclarés" value={formatJours(feuille.total_half_days)} />
      <Card>
        {mois !== null && <GrilleMois mois={mois} valeurs={depuisEntries(feuille.entries)} aujourdhui={aujourdhuiLocal()} />}
        <Legende />
      </Card>
    </div>
  );
}

/**
 * La grille éditable et le bouton « Soumettre ».
 *
 * La saisie s'enregistre toute seule ; soumettre commence par envoyer ce qui
 * est en attente, puis demande confirmation : après, la feuille ne se modifie
 * plus tant qu'elle n'a pas été traitée.
 */
function Saisie({ feuille, onChange }: { feuille: Timesheet; onChange: (maj: Timesheet) => void }) {
  const { notify } = useToast();
  const auto = useAutoSave(feuille);
  const soumission = useApiMutation(api.submitTimesheet);
  const [confirmer, setConfirmer] = useState(false);
  const mois = parseMois(feuille.period_month);
  const total = totalDemiJournees(auto.valeurs);
  const aujourdhui = aujourdhuiLocal();

  async function soumettre() {
    const enregistree = await auto.flush();
    if (enregistree === null && auto.etat === "erreur") {
      return;
    }
    const maj = await soumission.run(feuille.id);
    if (maj === null) {
      return;
    }
    setConfirmer(false);
    notify(
      maj.status === "approved"
        ? {
            tone: "success",
            title: "Feuille soumise et imputée",
            description: "Le client et votre commercial en sont informés.",
          }
        : {
            tone: "success",
            title: "Feuille soumise",
            description: "Le client et votre commercial sont prévenus. Vous serez informé(e) de la décision.",
          },
    );
    onChange(maj);
  }

  const texteEtat = {
    propre: "",
    modifie: "Modifications en attente…",
    enregistrement: "Enregistrement…",
    enregistre: "Brouillon enregistré.",
    erreur: `Enregistrement impossible : ${messageFeuille(auto.erreur)}`,
  }[auto.etat];

  return (
    <div className={styles.fiche}>
      <FeuilleStatut feuille={feuille} />
      <Stat label="Jours déclarés" value={formatJours(total)} hint="Le total se met à jour à chaque clic." />
      <Card>
        {mois !== null && (
          <GrilleMois
            mois={mois}
            valeurs={auto.valeurs}
            aujourdhui={aujourdhui}
            onChange={auto.setJour}
            disabled={soumission.pending}
          />
        )}
        <Legende />
        <p className={`${styles.enregistrement} ${auto.etat === "erreur" ? styles.enregistrementErreur : ""}`} aria-live="polite">
          {texteEtat}
        </p>
      </Card>

      {soumission.error !== null && (
        <p className={styles.refus} role="alert">
          {messageFeuille(soumission.error)}
        </p>
      )}

      <div className={styles.actions}>
        <Button
          variant="primary"
          onClick={() => setConfirmer(true)}
          disabled={total === 0 || auto.etat === "enregistrement"}
          loading={soumission.pending}
        >
          {feuille.requires_validation ? "Soumettre la feuille" : "Soumettre et imputer"}
        </Button>
        {total === 0 && <span className={styles.mention}>Déclarez au moins une demi-journée pour soumettre.</span>}
      </div>

      <ConfirmDialog
        open={confirmer}
        title="Soumettre cette feuille ?"
        description={
          feuille.requires_validation
            ? `${formatJours(total)} seront envoyés à ${feuille.company_name} et à votre commercial pour validation. La feuille ne sera plus modifiable tant qu'elle n'aura pas été traitée.`
            : `${formatJours(total)} seront imputés sur la mission dès la soumission, et ${feuille.company_name} en sera informé. La feuille ne sera plus modifiable.`
        }
        confirmLabel={feuille.requires_validation ? "Soumettre" : "Soumettre et imputer"}
        pending={soumission.pending}
        onConfirm={() => void soumettre()}
        onCancel={() => setConfirmer(false)}
      />
    </div>
  );
}
